import { useCallback, useEffect, useRef, useState } from "react";

/**
 * QR scanning from the device camera.
 *
 * Two decoders, picked at runtime. The browser's own `BarcodeDetector` is free
 * and hardware-accelerated, but Chrome only ships it on Android, ChromeOS and
 * macOS — not Windows, not Linux, and Safari and Firefox not at all. Betting on
 * it alone would give karigars on Android a working scanner and everybody else
 * a dead camera, including whoever is demonstrating the app from a laptop.
 *
 * So when it is missing we fall back to `jsqr`, pulled in with a dynamic
 * `import()` so it becomes its own chunk: the Android phones that make up most
 * of the audience never download it, and nobody else is left without a camera.
 *
 * What remains genuinely unavailable is kept in separate states, because each
 * has a different remedy and only one of them is the karigar's to fix:
 *
 *   insecure — the page is on plain HTTP, so the browser blocks the camera
 *              outright. Nobody on the phone can fix this; the site needs TLS.
 *   denied   — camera permission was refused; it can be granted again.
 *   error    — no camera, or it is held by another app.
 */
export type ScannerState = "starting" | "running" | "insecure" | "unsupported" | "denied" | "error";

/* `BarcodeDetector` is not in TypeScript's DOM library yet. */
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

/**
 * Fast enough to feel instant, slow enough not to cook the battery or pin a
 * low-end CPU at 100% while someone lines a sticker up.
 */
const DETECT_INTERVAL_MS = 200;

/**
 * The software decoder works on pixels, so the frame has to be copied to a
 * canvas first. Scanning it at full sensor resolution is wasted work — a QR
 * filling a third of the frame is still far above the decoder's threshold at
 * this size, and the downscale is what keeps the fallback usable on a slow
 * phone.
 */
const FALLBACK_EDGE_PX = 480;

type Decode = (video: HTMLVideoElement) => Promise<string | null>;

/** Native first; otherwise fetch `jsqr` as its own chunk. */
async function buildDecoder(): Promise<Decode> {
  const Native = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;

  if (Native) {
    const detector = new Native({ formats: ["qr_code"] });
    return async (video) => {
      const found = await detector.detect(video);
      return found[0]?.rawValue?.trim() ?? null;
    };
  }

  const { default: jsQR } = await import("jsqr");
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  return async (video) => {
    if (!ctx) return null;
    const scale = Math.min(1, FALLBACK_EDGE_PX / Math.max(video.videoWidth, video.videoHeight));
    const w = Math.round(video.videoWidth * scale);
    const h = Math.round(video.videoHeight * scale);
    if (w === 0 || h === 0) return null;

    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(video, 0, 0, w, h);
    const found = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, {
      inversionAttempts: "dontInvert",
    });
    return found?.data.trim() ?? null;
  };
}

interface Options {
  /** Called once per distinct code. The hook stops the camera before calling. */
  onCode: (raw: string) => void;
  /** Set false to release the camera — e.g. while a result is on screen. */
  enabled: boolean;
}

export function useQrScanner({ onCode, enabled }: Options) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  const [state, setState] = useState<ScannerState>("starting");
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [attempt, setAttempt] = useState(0);

  /** Re-request the camera after the user answers a permission prompt. */
  const retry = useCallback(() => {
    setState("starting");
    setAttempt((n) => n + 1);
  }, []);

  const toggleTorch = useCallback(() => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    /*
     * `torch` is a real constraint on Android Chrome but is absent from the
     * standard `MediaTrackConstraintSet` type, hence the cast. A failure is not
     * worth surfacing — the scan still works, only dimmer.
     */
    void track
      .applyConstraints({ advanced: [{ torch: next }] } as unknown as MediaTrackConstraints)
      .then(() => setTorchOn(next))
      .catch(() => setTorchAvailable(false));
  }, [torchOn]);

  useEffect(() => {
    if (!enabled) return;

    if (!window.isSecureContext) {
      setState("insecure");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setState("unsupported");
      return;
    }

    let cancelled = false;
    let timer: number | null = null;
    let stream: MediaStream | null = null;

    async function start(): Promise<void> {
      let decode: Decode;
      try {
        decode = await buildDecoder();
      } catch {
        if (!cancelled) setState("unsupported");
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        });
      } catch (err) {
        if (cancelled) return;
        const name = err instanceof DOMException ? err.name : "";
        setState(name === "NotAllowedError" || name === "SecurityError" ? "denied" : "error");
        return;
      }

      /*
       * The effect can be torn down while `getUserMedia` is still resolving —
       * React 19 StrictMode does exactly this on mount. Without the check the
       * camera light stays on with no component left to show it.
       */
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        // Autoplay was refused; frames still arrive for the decoder.
      }
      if (cancelled) return;

      const track = stream.getVideoTracks()[0];
      const caps = track?.getCapabilities?.() as { torch?: boolean } | undefined;
      setTorchAvailable(Boolean(caps?.torch));
      setState("running");

      const tick = async (): Promise<void> => {
        if (cancelled || !videoRef.current) return;
        try {
          /*
           * `readyState` guards the first frames: decoding a video that has no
           * dimensions yet throws on some builds rather than returning nothing.
           */
          if (videoRef.current.readyState >= 2) {
            const raw = await decode(videoRef.current);
            if (raw && !cancelled) {
              cancelled = true; // one code per camera session
              onCodeRef.current(raw);
              return;
            }
          }
        } catch {
          // A single failed frame is normal — keep looking.
        }
        if (!cancelled) timer = window.setTimeout(() => void tick(), DETECT_INTERVAL_MS);
      };

      void tick();
    }

    void start();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setTorchOn(false);
    };
  }, [enabled, attempt]);

  return { videoRef, state, torchAvailable, torchOn, toggleTorch, retry };
}
